import openai from '../lib/openai';
import { supabase } from '../lib/supabase';
import OpenAI, {
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * AI Quiz Generation Service
 * Generates contextual quiz questions based on play content using OpenAI GPT-5
 */
export const aiQuizGenerationService = {
  /**
   * Generate quiz questions for a play based on selected scenes and characters
   * @param {Object} options - Generation options
   * @param {string} options.playId - Play UUID
   * @param {Array<{act: number, scene: number}>} options.scenes - Selected scenes
   * @param {Array<string>} options.characters - Selected character names
   * @param {string} options.difficulty - Difficulty level: 'easy', 'medium', 'hard'
   * @param {number} options.questionCount - Number of questions to generate
   * @param {Array<string>} options.questionTypes - Types of questions to include
   * @returns {Promise<Array>} Generated quiz questions
   */
  async generateQuizQuestions({
    playId,
    scenes = [],
    characters = [],
    difficulty = 'medium',
    questionCount = 10,
    questionTypes = ['multiple_choice', 'character_matching']
  }) {
    try {
      // 1. Fetch play details
      const { data: play, error: playError } = await supabase
        ?.from('plays')
        ?.select('id, title, author')
        ?.eq('id', playId)
        ?.single();

      if (playError) throw playError;
      if (!play) throw new Error('Play not found');

      // 2. Fetch content from selected scenes
      const sceneContent = await this._fetchSceneContent(playId, scenes);

      // 3. Generate contextual prompt
      const prompt = this._buildQuizGenerationPrompt({
        playTitle: play?.title,
        author: play?.author,
        sceneContent,
        characters,
        difficulty,
        questionCount,
        questionTypes
      });

      // 4. Call OpenAI API with structured output
      const generatedQuestions = await this._callOpenAIForQuestions(prompt, questionCount);

      return generatedQuestions;
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating quiz questions:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Fetch content from selected scenes
   * @private
   */
  async _fetchSceneContent(playId, scenes) {
    const scenePromises = scenes?.map(async ({ act, scene }) => {
      const { data: sceneData, error: sceneError } = await supabase
        ?.from('scenes')
        ?.select('id, title, description')
        ?.eq('play_id', playId)
        ?.eq('act_number', act)
        ?.eq('scene_number', scene)
        ?.single();

      if (sceneError) throw sceneError;

      const { data: linesData, error: linesError } = await supabase
        ?.from('lines')
        ?.select('character, dialogue, is_stage_direction')
        ?.eq('scene_id', sceneData?.id)
        ?.order('line_number', { ascending: true });

      if (linesError) throw linesError;

      return {
        act,
        scene,
        title: sceneData?.title,
        description: sceneData?.description,
        lines: linesData || []
      };
    });

    const sceneContents = await Promise.all(scenePromises);
    return sceneContents;
  },

  /**
   * Build prompt for OpenAI question generation
   * @private
   */
  _buildQuizGenerationPrompt({
    playTitle,
    author,
    sceneContent,
    characters,
    difficulty,
    questionCount,
    questionTypes
  }) {
    const scenesText = sceneContent?.map(scene => {
      const linesText = scene?.lines
        ?.map(line => {
          if (line?.is_stage_direction) {
            return `[${line?.dialogue}]`;
          }
          return `${line?.character}: ${line?.dialogue}`;
        })
        ?.join('\n');

      return `Act ${scene?.act}, Scene ${scene?.scene} - ${scene?.title}\n${linesText}`;
    })?.join('\n\n');

    const characterList = characters?.length > 0 
      ? `Focus on these characters: ${characters?.join(', ')}`
      : 'Include questions about all major characters in these scenes';

    const questionTypeDescriptions = {
      multiple_choice: 'Multiple choice questions with 4 options',
      character_matching: 'Match characters to their quotes or actions',
      true_false: 'True/False questions about plot events'
    };

    const requestedTypes = questionTypes
      ?.map(type => questionTypeDescriptions?.[type])
      ?.join(', ');

    return `You are an expert Shakespeare educator creating a ${difficulty} level quiz for "${playTitle}" by ${author}.

PLAY CONTENT:
${scenesText}

INSTRUCTIONS:
- Generate ${questionCount} high-quality quiz questions
- ${characterList}
- Difficulty level: ${difficulty}
- Question types: ${requestedTypes}
- Questions should test deep understanding, not just memorization
- Each question must be specific to the provided scenes
- For ${difficulty} difficulty:
  * easy: Focus on basic comprehension and character identification
  * medium: Include theme analysis and character motivation
  * hard: Require critical analysis, symbolism, and complex relationships

FORMAT REQUIREMENTS:
- For multiple_choice: Provide 4 options with exactly one correct answer
- For character_matching: Provide 4 characters and 4 quotes/actions to match
- For true_false: Create statements that require understanding of the text
- Always include a detailed explanation for the correct answer
- Make incorrect options plausible but clearly wrong upon analysis

EDUCATIONAL FOCUS:
- Test comprehension of plot events
- Evaluate character understanding
- Assess thematic awareness
- Check language and vocabulary comprehension
- Measure critical thinking about the text`;
  },

  /**
   * Call OpenAI API with structured output for quiz questions
   * @private
   */
  async _callOpenAIForQuestions(prompt, questionCount) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5',
        messages: [
          { 
            role: 'system', 
            content: 'You are an expert Shakespeare educator specializing in creating engaging, pedagogically sound quiz questions. Generate questions that test deep understanding and critical thinking, not just memorization.' 
          },
          { role: 'user', content: prompt }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'quiz_questions_response',
            schema: {
              type: 'object',
              properties: {
                questions: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      question_text: { type: 'string', description: 'The question text' },
                      question_type: { 
                        type: 'string', 
                        enum: ['multiple_choice', 'character_matching', 'true_false'],
                        description: 'Type of question'
                      },
                      difficulty_level: { 
                        type: 'string', 
                        enum: ['easy', 'medium', 'hard'],
                        description: 'Question difficulty'
                      },
                      options: { 
                        type: 'object',
                        description: 'For multiple_choice: {A: string, B: string, C: string, D: string}. For character_matching: {character1: quote1, character2: quote2, etc.}'
                      },
                      correct_answer: { 
                        type: 'string',
                        description: 'For multiple_choice: the letter (A, B, C, D). For character_matching: JSON string of correct matches. For true_false: "true" or "false"'
                      },
                      explanation: { 
                        type: 'string',
                        description: 'Detailed explanation of why the answer is correct'
                      },
                      points: {
                        type: 'number',
                        description: 'Points awarded for correct answer (1-5 based on difficulty)'
                      }
                    },
                    required: ['question_text', 'question_type', 'difficulty_level', 'options', 'correct_answer', 'explanation', 'points']
                  }
                }
              },
              required: ['questions'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'high',
        verbosity: 'medium'
      });

      const result = JSON.parse(response?.choices?.[0]?.message?.content);
      return result?.questions || [];
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error calling OpenAI for questions:', error);
      }
      throw new Error(errorInfo?.message);
    }
  },

  /**
   * Create quiz and questions in database
   * @param {Object} quizData - Quiz metadata
   * @param {Array} questions - Generated questions
   * @param {string} userId - User ID creating the quiz
   * @returns {Promise<Object>} Created quiz with questions
   */
  async saveGeneratedQuiz(quizData, questions, userId) {
    try {
      // 1. Create quiz record
      const { data: quiz, error: quizError } = await supabase
        ?.from('quizzes')
        ?.insert([{
          ...quizData,
          created_by: userId,
          is_published: false
        }])
        ?.select()
        ?.single();

      if (quizError) throw quizError;

      // 2. Create quiz questions
      const questionsToInsert = questions?.map((q, index) => ({
        quiz_id: quiz?.id,
        question_text: q?.question_text,
        question_type: q?.question_type,
        difficulty_level: q?.difficulty_level,
        options: q?.options,
        correct_answer: q?.correct_answer,
        explanation: q?.explanation,
        points: q?.points,
        order_index: index
      }));

      const { data: insertedQuestions, error: questionsError } = await supabase
        ?.from('quiz_questions')
        ?.insert(questionsToInsert)
        ?.select();

      if (questionsError) throw questionsError;

      return {
        quiz,
        questions: insertedQuestions
      };
    } catch (error) {
      console.error('Error saving generated quiz:', error);
      throw error;
    }
  },

  /**
   * Get all characters from a play for selection
   * @param {string} playId - Play UUID
   * @returns {Promise<Array<string>>} List of unique character names
   */
  async getPlayCharacters(playId) {
    try {
      const { data, error } = await supabase
        ?.from('lines')
        ?.select('character')
        ?.eq('play_id', playId)
        ?.not('character', 'is', null)
        ?.not('is_stage_direction', 'eq', true);

      if (error) throw error;

      const uniqueCharacters = [...new Set(data?.map(line => line?.character))];
      return uniqueCharacters?.sort();
    } catch (error) {
      console.error('Error fetching play characters:', error);
      throw error;
    }
  }
};